import {
  ArrowLeftOutlined,
  DeleteOutlined,
  ExportOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ConnectivityStatus } from '@evimo/types/enums/connectivity-status';
import { formatDateTime } from '~/app/utils/format-date-time';
import { getPublicAssetUrl } from '~/app/utils/getPublicAssetUrl';
import { AssetLocationMap } from './AssetLocationMap';
import styles from './AssetDetailHero.module.css';

type AssetDetailHeroProps = {
  assetName: string;
  serialNumber?: string | null;
  assetTypeName?: string;
  assetModelName?: string | null;
  connectivityStatus?: ConnectivityStatus | null;
  lastSeenAt?: string | null;
  onBack: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
  onReset?: () => void;
  onSubmit?: () => void;
  hasChanges?: boolean;
  isSaving?: boolean;
  mapLatitude: number | null;
  mapLongitude: number | null;
  mapTitle: string;
  onMapClick?: () => void;
};

function ConnectivityPill({
  status,
  lastSeenAt,
}: {
  status: ConnectivityStatus | null | undefined;
  lastSeenAt?: string | null;
}) {
  const { t } = useTranslation('assets');

  if (!status) {
    return null;
  }

  const lastSeen = lastSeenAt
    ? t('overview.table.lastSeenTooltip', 'Last seen: {{date}}', {
        date: formatDateTime(lastSeenAt),
      })
    : t('overview.table.neverSeen', 'Never seen');

  if (status === ConnectivityStatus.UNKNOWN) {
    return (
      <Tooltip
        title={`${t(
          'overview.table.unknownTooltip',
          'The platform cannot measure connectivity right now',
        )}. ${lastSeen}`}
      >
        <span className={['ds-pill', 'ds-pill-neutral'].join(' ')}>
          {t('overview.table.unknown', 'Unknown')}
        </span>
      </Tooltip>
    );
  }

  const isOnline = status === ConnectivityStatus.ONLINE;

  return (
    <Tooltip title={lastSeen}>
      <span
        className={[
          'ds-pill',
          isOnline ? 'ds-pill-success' : 'ds-pill-danger',
        ].join(' ')}
      >
        {isOnline
          ? t('overview.table.online', 'Online')
          : t('overview.table.offline', 'Offline')}
      </span>
    </Tooltip>
  );
}

export function AssetDetailHero({
  assetName,
  serialNumber,
  assetTypeName,
  assetModelName,
  connectivityStatus,
  lastSeenAt,
  onBack,
  onDelete,
  isDeleting = false,
  onReset,
  onSubmit,
  hasChanges = false,
  isSaving = false,
  mapLatitude,
  mapLongitude,
  mapTitle,
  onMapClick,
}: AssetDetailHeroProps) {
  const { t } = useTranslation('assets');
  const chargerIconSrc = getPublicAssetUrl('assets/ev-charger.png');
  const hasMetaLabel = Boolean(assetTypeName || assetModelName);

  return (
    <div className={styles.heroContainer}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        className={styles.backButton}
      >
        {t('detail.backToList', 'Back to assets')}
      </Button>

      <div className={styles.heroCard}>
        <div className={styles.heroRow}>
          <div className={styles.identity}>
            <div className={styles.iconBox} aria-hidden="true">
              <img
                src={chargerIconSrc}
                alt=""
                className={styles.iconImage}
                width={28}
                height={28}
              />
            </div>

            <div className={styles.identityText}>
              <div className={styles.titleRow}>
                <p className={styles.heroTitle}>{assetName}</p>
                <ConnectivityPill
                  status={connectivityStatus}
                  lastSeenAt={lastSeenAt}
                />
              </div>

              <div className={styles.metaRow}>
                {serialNumber ? (
                  <Tooltip title={serialNumber}>
                    <span className={styles.serialChip}>
                      <span className={styles.serialText}>{serialNumber}</span>
                    </span>
                  </Tooltip>
                ) : null}
                {serialNumber && hasMetaLabel ? (
                  <span className={styles.metaDivider} aria-hidden="true" />
                ) : null}
                {assetTypeName ? (
                  <span className={styles.metaText}>{assetTypeName}</span>
                ) : null}
                {assetTypeName && assetModelName ? (
                  <span className={styles.metaDot} aria-hidden="true">
                    ·
                  </span>
                ) : null}
                {assetModelName ? (
                  <span className={styles.metaText}>{assetModelName}</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className={styles.aside}>
            <div className={styles.divider} aria-hidden="true" />

            <button
              type="button"
              className={styles.mapPreview}
              aria-label={t(
                'detail.map.previewAria',
                'Asset location map preview. Opens location details.',
              )}
              disabled={!onMapClick}
              onClick={onMapClick}
            >
              <AssetLocationMap
                latitude={mapLatitude}
                longitude={mapLongitude}
                title={mapTitle}
                fillContainer
                showZoomControl={false}
                unavailableText={t(
                  'detail.map.unavailable',
                  'No coordinates are available for this site yet.',
                )}
                missingKeyText={t(
                  'detail.map.missingKey',
                  'Missing Google Maps API key. Add VITE_PUBLIC_GOOGLE_MAPS_API_KEY to apps/web/.env.',
                )}
              />
              {onMapClick ? (
                <span className={styles.mapOpenHint} aria-hidden="true">
                  <ExportOutlined />
                </span>
              ) : null}
            </button>

            <div className={styles.actions}>
              {onDelete ? (
                <Button
                  danger
                  block
                  icon={<DeleteOutlined />}
                  loading={isDeleting}
                  onClick={onDelete}
                  className={styles.actionButton}
                >
                  {t('delete.button', 'Delete')}
                </Button>
              ) : null}
              {onReset ? (
                <Button
                  block
                  icon={<ReloadOutlined />}
                  disabled={!hasChanges || isSaving}
                  onClick={onReset}
                  className={styles.actionButton}
                >
                  {t('detail.actions.reset', 'Reset')}
                </Button>
              ) : null}
              {onSubmit ? (
                <Button
                  block
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={isSaving}
                  disabled={!hasChanges}
                  onClick={onSubmit}
                  className={styles.actionButton}
                >
                  {t('detail.actions.save', 'Save changes')}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
